import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { fetchWithAuth } from "../utils/fetchWithAuth";

interface OrgSummary {
  orgId: string;
  name: string;
}

interface OrgContextValue {
  activeOrg: OrgSummary | null;
  userOrgs: OrgSummary[];
  setActiveOrg: (org: OrgSummary) => void;
  refreshOrgs: () => Promise<void>;
  loading: boolean;
}

const OrgContext = createContext<OrgContextValue | null>(null);

const ACTIVE_ORG_KEY = "activeOrgId";

export function OrgProvider({ children }: { children: React.ReactNode }) {
  const [userOrgs, setUserOrgs] = useState<OrgSummary[]>([]);
  const [activeOrg, setActiveOrgState] = useState<OrgSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const refreshOrgs = useCallback(async () => {
    try {
      const response = await fetchWithAuth("/api/organisations");
      if (!response.ok) return;
      const orgs: OrgSummary[] = await response.json();
      setUserOrgs(orgs);

      const storedOrgId = localStorage.getItem(ACTIVE_ORG_KEY);
      const stored = orgs.find((o) => o.orgId === storedOrgId);
      const selected = stored ?? orgs[0] ?? null;
      setActiveOrgState(selected);
      if (selected) {
        localStorage.setItem(ACTIVE_ORG_KEY, selected.orgId);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    refreshOrgs();
  }, [authLoading, isAuthenticated, refreshOrgs]);

  const setActiveOrg = useCallback((org: OrgSummary) => {
    setActiveOrgState(org);
    localStorage.setItem(ACTIVE_ORG_KEY, org.orgId);
  }, []);

  return (
    <OrgContext.Provider value={{ activeOrg, userOrgs, setActiveOrg, refreshOrgs, loading }}>
      {children}
    </OrgContext.Provider>
  );
}

export function useOrgContext(): OrgContextValue {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error("useOrgContext must be used inside OrgProvider");
  return ctx;
}
